import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, runTransaction, serverTimestamp,
  Timestamp, limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, functionsEast1, sendPushToUser } from '../firebase';
import {
  OrderStatus, DriverOrderStatus, DRIVER_TO_ORDER_STATUS, ACTIVE_DRIVER_STATUSES,
  MAX_ACTIVE_DELIVERIES, DRIVER_COMMISSION_RATE, PLATFORM_COMMISSION_RATE,
  RESTAURANT_PAYOUT_RATE, parseOrder, parseAcceptedOrder, parseDriverStatus,
  orderMatchesDriverIsland,
} from '../models';
import { round2 } from '../../../../utils/commission';

const ordersCol = collection(db, 'orders');

// ---------- Fetch / listeners ----------

export async function fetchOrder(orderId) {
  const snap = await getDoc(doc(db, 'orders', orderId));
  return snap.exists() ? parseOrder(snap.id, snap.data()) : null;
}

export function listenToOrder(orderId, cb, onErr) {
  return onSnapshot(doc(db, 'orders', orderId),
    s => cb(s.exists() ? parseOrder(s.id, s.data()) : null),
    e => { console.error('listenToOrder', e); onErr && onErr(e); });
}

// All orders for a restaurant, newest first.
export function listenToRestaurantOrders(restaurantId, cb, onErr) {
  const q = query(ordersCol, where('restaurantId', '==', restaurantId));
  return onSnapshot(q,
    snap => cb(snap.docs.map(d => parseOrder(d.id, d.data()))),
    e => { console.error('listenToRestaurantOrders', e); onErr && onErr(e); });
}

// Ready + unassigned + delivery orders (iOS filters driverId client-side).
// iOS also gates on island: DriverManager only surfaces orders where
// restaurantLocation.island == customerLocation.island == driver.island —
// a St. Kitts driver must never see a Nevis order (and vice versa).
export function listenToAvailableOrders(cb, onErr, driverIsland = null) {
  const q = query(ordersCol, where('status', '==', OrderStatus.READY));
  return onSnapshot(q, snap => {
    const orders = snap.docs
      .map(d => parseOrder(d.id, d.data()))
      .filter(o => !o.driverId && (o.orderType || '').toLowerCase() === 'delivery')
      .filter(o => orderMatchesDriverIsland(o, driverIsland));
    cb(orders);
  }, e => { console.error('listenToAvailableOrders', e); onErr && onErr(e); });
}

export function listenToAcceptedOrders(driverId, cb, onErr) {
  const col = collection(db, 'drivers', driverId, 'acceptedOrders');
  return onSnapshot(col, snap => {
    cb(snap.docs.map(d => parseAcceptedOrder(d.id, d.data())));
  }, e => { console.error('listenToAcceptedOrders', e); onErr && onErr(e); });
}

export function listenToAcceptedOrder(driverId, orderId, cb, onErr) {
  const ref = doc(db, 'drivers', driverId, 'acceptedOrders', orderId);
  return onSnapshot(ref,
    s => cb(s.exists() ? parseAcceptedOrder(s.id, s.data()) : null),
    e => { console.error('listenToAcceptedOrder', e); onErr && onErr(e); });
}

export async function fetchAcceptedOrders(driverId) {
  const snap = await getDocs(collection(db, 'drivers', driverId, 'acceptedOrders'));
  return snap.docs.map(d => parseAcceptedOrder(d.id, d.data()));
}

export function activeDeliveries(accepted) {
  return accepted.filter(o => ACTIVE_DRIVER_STATUSES.has(parseDriverStatus(o.raw?.status || o.status)));
}

// ---------- Notifications ----------

export async function createNotification({ userId, type, title, message, orderId, restaurantId, driverId, data }) {
  if (!userId) return null;
  const docData = {
    userId,
    type: type || 'order_update',
    title: title || '',
    message: message || '',
    orderId: orderId ?? null,
    restaurantId: restaurantId ?? null,
    driverId: driverId ?? null,
    isRead: false,
    createdAt: Timestamp.now(),
  };
  // Rules only allow `data` keys: arrivalImageUrl/orderId/restaurantId/driverId —
  // and a null `data` field fails the .keys() check, so omit it when empty.
  if (data && typeof data === 'object') {
    const allowed = ['arrivalImageUrl', 'orderId', 'restaurantId', 'driverId'];
    const clean = Object.fromEntries(
      Object.entries(data).filter(([k]) => allowed.includes(k))
    );
    if (Object.keys(clean).length) docData.data = clean;
  }
  const docRef = await addDoc(collection(db, 'notifications'), docData);
  return docRef.id;
}

// Find the restaurant owner's user doc id — iOS queries
// users where restaurantId == X && role == 'restaurant'.
export async function getRestaurantOwnerUserId(restaurantId) {
  if (!restaurantId) return null;
  try {
    const q = query(
      collection(db, 'users'),
      where('restaurantId', '==', restaurantId),
      where('role', '==', 'restaurant'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) return snap.docs[0].id;
    // Fallbacks: any user with the restaurantId, then the restaurant doc.
    const q2 = query(collection(db, 'users'), where('restaurantId', '==', restaurantId), limit(1));
    const snap2 = await getDocs(q2);
    if (!snap2.empty) return snap2.docs[0].id;
    const rs = await getDoc(doc(db, 'restaurants', restaurantId));
    const d = rs.data() || {};
    return d.ownerId || d.userId || null;
  } catch {
    return null;
  }
}

// In-app notification + FCM push to a user (mirrors iOS side-effects).
export async function notifyUser(userId, { type, title, message, orderId, restaurantId, driverId, pushBody }) {
  await createNotification({ userId, type, title, message, orderId, restaurantId, driverId });
  await sendPushToUser(userId, {
    notification: { title, body: pushBody || message },
    data: {
      type: type || 'order_update',
      ...(orderId ? { orderId } : {}),
      ...(restaurantId ? { restaurantId } : {}),
    },
  });
}

async function notifyCustomer(order, title, message, type = 'order_update', status, data) {
  if (!order.customerId) return;
  await createNotification({
    userId: order.customerId, type, title, message,
    orderId: order.id, restaurantId: order.restaurantId, driverId: order.driverId,
    data,
  });
  await sendPushToUser(order.customerId, {
    notification: { title, body: message },
    data: { type, orderId: order.id, ...(status ? { status } : {}), ...(data || {}) },
  });
}

// iOS DriverDashboardViewModel.createDriverOrderReadyNotification — every
// online driver gets an in-app "New Order Ready" record when an order flips
// to Ready (separate from the local sound/banner).
export async function createDriverOrderReadyNotification(order, driverId) {
  await createNotification({
    userId: driverId,
    type: 'delivery_update',
    title: 'New Order Ready',
    message: `New order ready for pickup at ${order.restaurantLocation?.name || order.restaurantName || 'the restaurant'}`,
    orderId: order.id,
    driverId,
    data: { orderId: order.id },
  }).catch(e => console.warn('createDriverOrderReadyNotification:', e));
}

async function notifyRestaurant(order, title, message, type = 'order_update') {
  const ownerId = await getRestaurantOwnerUserId(order.restaurantId);
  if (ownerId) {
    await notifyUser(ownerId, {
      type, title, message,
      orderId: order.id, restaurantId: order.restaurantId, driverId: order.driverId,
    });
  }
}

// ---------- Generic order status update (restaurant side) ----------

// Mirrors FirebaseManager.updateOrderStatus: status + updatedAt, and marks
// paymentStatus 'succeeded' once the order is being worked (Confirmed+).
export async function updateOrderStatus(orderId, status, extra = {}) {
  const data = { status, updatedAt: serverTimestamp(), ...extra };
  await updateDoc(doc(db, 'orders', orderId), data);
  return fetchOrder(orderId);
}

// Restaurant accept: capture the JAD-authorized payment first, then mark Confirmed.
export async function restaurantAcceptOrder(order) {
  const latest = await fetchOrder(order.id || order.orderId);
  if (!latest) throw new Error('Order not found');
  const st = (latest.rawStatus || '').toLowerCase();
  if (st === 'cancelled') throw new Error('Cannot accept a cancelled order');
  if (latest.status !== OrderStatus.PENDING) throw new Error(`Order is already ${latest.status}`);

  if (latest.paymentProvider === 'jad' || latest.jadAuthorizationId) {
    const capture = httpsCallable(functions, 'jadCapture');
    await capture({ orderId: latest.id });
  }
  const updated = await updateOrderStatus(latest.id, OrderStatus.CONFIRMED);
  await notifyCustomer(latest, '✅ Order Confirmed!',
    `Great news! ${latest.restaurantName} has confirmed your order #${latest.orderNumber}.`, 'order_status');
  return updated;
}

// Restaurant reject: cancel payment auth + mark Cancelled + notify customer.
export async function restaurantRejectOrder(order, reason = '') {
  const latest = await fetchOrder(order.id || order.orderId);
  if (!latest) throw new Error('Order not found');
  const st = (latest.rawStatus || '').toLowerCase();
  if (st === 'cancelled') throw new Error('Order is already cancelled');
  if (latest.status !== OrderStatus.PENDING) throw new Error(`Cannot reject an order that is ${latest.status}`);

  if (latest.paymentProvider === 'jad' || latest.jadAuthorizationId) {
    try {
      const void_ = httpsCallable(functions, 'jadVoid');
      await void_({ orderId: latest.id, reason: 'Order rejected by restaurant' });
    } catch (e) {
      console.warn('jadVoid failed (continuing):', e);
    }
  }
  const updated = await updateOrderStatus(latest.id, OrderStatus.CANCELLED, {
    cancellationReason: reason || 'Rejected by restaurant',
    cancelledBy: 'restaurant',
  });
  await notifyCustomer(latest, '❌ Order Cancelled',
    `We're sorry, ${latest.restaurantName} had to cancel your order #${latest.orderNumber}. You will not be charged.`, 'order_cancelled');
  await notifyAssignedDriver(latest,
    `Order #${(latest.id || '').slice(0, 8)} has been cancelled`);
  return updated;
}

export async function markOrderPreparing(order) {
  const updated = await updateOrderStatus(order.id, OrderStatus.PREPARING);
  await notifyCustomer(order, '👨‍🍳 Order Being Prepared',
    `${order.restaurantName} is now preparing your order #${order.orderNumber}.`, 'order_status');
  return updated;
}

// iOS OrderManager.createDriverNotification — the assigned driver gets
// delivery_update notifications for ready/cancelled statuses.
async function notifyAssignedDriver(order, message) {
  if (!order?.driverId) return;
  await createNotification({
    userId: order.driverId,
    type: 'delivery_update',
    title: 'Order Update',
    message,
    orderId: order.id,
    restaurantId: order.restaurantId,
    driverId: order.driverId,
  }).catch(e => console.warn('notifyAssignedDriver:', e));
}

export async function markOrderReady(order) {
  const updated = await updateOrderStatus(order.id, OrderStatus.READY);
  await notifyCustomer(order, '📦 Order Ready!',
    `Your order #${order.orderNumber} is ready for ${(order.orderType || '').toString().toLowerCase() === 'pickup' ? 'pickup' : 'driver pickup'}.`, 'order_ready');
  await notifyAssignedDriver(order,
    `New order ready for pickup at ${order.restaurantName || order.restaurantLocation?.name || 'the restaurant'}`);
  return updated;
}

export async function markOrderCompleted(order) {
  const updated = await updateOrderStatus(order.id, OrderStatus.COMPLETED);
  return updated;
}

// Cancel with a specific reason (RestaurantCancelOrderView) — works on any status.
export async function cancelOrderWithReason(order, reason, cancelledBy = 'restaurant') {
  if ((order.paymentProvider === 'jad' || order.jadAuthorizationId) && !['captured', 'succeeded'].includes(order.paymentStatus)) {
    try {
      const void_ = httpsCallable(functions, 'jadVoid');
      await void_({ orderId: order.id, reason });
    } catch (e) {
      console.warn('jadVoid failed (continuing):', e);
    }
  }
  const updated = await updateOrderStatus(order.id, OrderStatus.CANCELLED, {
    cancellationReason: reason,
    cancelledBy,
  });
  await notifyCustomer(order, '❌ Order Cancelled',
    `Your order #${order.orderNumber} was cancelled. Reason: ${reason}. If payment was captured a refund will be requested.`, 'order_cancelled');
  await notifyAssignedDriver(order,
    `Order #${(order.id || '').slice(0, 8)} has been cancelled`);
  return updated;
}

// ---------- Driver acceptance (mirrors FirebaseManager.acceptOrder) ----------

export async function driverAcceptOrder(order, driver) {
  const driverId = driver.id || driver.uid;
  const driverName = driver.name || driver.fullName || 'Driver';

  // Capacity check: max 2 incomplete deliveries.
  const accepted = await fetchAcceptedOrders(driverId);
  const incomplete = activeDeliveries(accepted);
  if (incomplete.length >= MAX_ACTIVE_DELIVERIES) {
    throw new Error('You already have 2 active deliveries. Complete one before accepting another.');
  }

  const orderRef = doc(db, 'orders', order.id);
  const acceptedRef = doc(db, 'drivers', driverId, 'acceptedOrders', order.id);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(orderRef);
    if (!snap.exists()) throw new Error('Order no longer exists');
    const d = snap.data();
    if (d.driverId) throw new Error('This order was already accepted by another driver');
    if (parseOrderStatus(d.status) !== OrderStatus.READY) {
      throw new Error('This order is no longer available');
    }
    const deliveryFee = d.deliveryFee || 0;
    const tipAmount = d.tipAmount || 0;
    const driverCommission = round2(deliveryFee * DRIVER_COMMISSION_RATE);
    // Platform commission = 10% of food subtotal + 10% of delivery fee
    // (COMMISSION_CALCULATIONS_AND_PAYOUTS.md). iOS stores only the food
    // portion at creation and only the delivery portion at accept — the
    // combined value is written here so the order doc reconciles.
    const subtotal = d.subtotal || 0;
    const platformCommission = round2(
      (subtotal * PLATFORM_COMMISSION_RATE) + (deliveryFee * PLATFORM_COMMISSION_RATE)
    );
    const driverPayout = round2(driverCommission + tipAmount);

    tx.update(orderRef, {
      driverId,
      driverName,
      status: OrderStatus.CONFIRMED,
      driverCommission,
      platformCommission,
      driverPayout,
      updatedAt: serverTimestamp(),
    });

    // iOS writes order details nested under `orderDetails` (FirebaseManager
    // .acceptOrder). Keep the flat fields for web readers AND the nested
    // shape so the iOS driver app can read web-accepted orders.
    const iOSItems = (d.orderItems || d.items || []).map(item => ({
      id: item.id || item.menuItemId || '',
      name: item.menuItem?.name || item.menuItemName || item.name || '',
      price: item.menuItem?.price ?? item.price ?? 0,
      quantity: item.quantity ?? 0,
      subtotal: (item.menuItem?.price ?? item.price ?? 0) * (item.quantity ?? 0),
    }));
    const orderDetails = {
      customerId: d.customerId || '',
      customerName: d.customerName || '',
      customerPhone: d.customerPhone || '',
      customerAddress: d.customerAddress || d.deliveryAddress?.street || '',
      restaurantId: d.restaurantId || '',
      restaurantName: d.restaurantName || '',
      subtotal,
      chefServiceFee: d.chefServiceFee ?? d.serviceFee ?? 0,
      totalAmount: d.totalAmount || 0,
      specialInstructions: d.specialInstructions || '',
      items: iOSItems,
    };

    tx.set(acceptedRef, {
      orderId: order.id,
      driverId,
      status: DriverOrderStatus.ACCEPTED,
      acceptedAt: serverTimestamp(),
      estimatedPickupTime: d.estimatedPickupTime || null,
      actualPickupTime: null,
      estimatedDeliveryTime: d.estimatedDeliveryTime || null,
      actualDeliveryTime: null,
      pickupLocation: d.restaurantLocation?.coordinates || d.pickupLocation || null,
      deliveryLocation: d.deliveryLocation || d.deliveryAddress?.coordinates || null,
      totalEarnings: driverPayout,
      driverPayout,
      driverCommission,
      platformCommission,
      deliveryFee,
      tipAmount,
      customerId: d.customerId || '',
      customerName: d.customerName || '',
      customerPhone: d.customerPhone || '',
      customerAddress: d.customerAddress || d.deliveryAddress?.street || '',
      deliveryAddress: d.deliveryAddress || null,
      restaurantId: d.restaurantId || '',
      restaurantName: d.restaurantName || '',
      orderItems: d.orderItems || d.items || [],
      orderDetails,
      subtotal,
      chefServiceFee: d.chefServiceFee ?? d.serviceFee ?? 0,
      totalAmount: d.totalAmount || 0,
      distance: d.distance || 0,
      specialInstructions: d.specialInstructions || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  const updated = await fetchOrder(order.id);
  await notifyRestaurant(updated, '🚗 Driver Assigned',
    `A driver has accepted order #${updated.orderNumber} and is heading to your restaurant.`, 'driver_accepted');
  await notifyCustomer(updated, '🚗 Driver Assigned',
    `A driver has been assigned to your order #${updated.orderNumber}.`, 'driver_accepted');
  return updated;
}

// ---------- Driver status progression ----------

// Mirrors OrderManager.updateDriverOrderStatus: writes accepted-order status,
// maps to the main order status, fires notifications, cleans up on delivery.
export async function updateDriverOrderStatus(driverId, orderId, newStatus, extra = {}) {
  const status = parseDriverStatus(newStatus) || newStatus;
  const acceptedRef = doc(db, 'drivers', driverId, 'acceptedOrders', orderId);
  await setDoc(acceptedRef, {
    status,
    updatedAt: serverTimestamp(),
    ...(status === DriverOrderStatus.PICKED_UP_ORDER ? { actualPickupTime: serverTimestamp() } : {}),
    ...(status === DriverOrderStatus.DELIVERED ? { actualDeliveryTime: serverTimestamp() } : {}),
    ...extra,
  }, { merge: true });

  const orderStatus = DRIVER_TO_ORDER_STATUS[status];
  const order = await fetchOrder(orderId);
  if (orderStatus && order && order.status !== orderStatus) {
    // Lifecycle timestamps live on the order record itself — the acceptedOrder
    // doc is deleted after delivery, so stats must be readable from orders.
    const extra = {};
    if (status === DriverOrderStatus.PICKED_UP_ORDER) {
      extra.restaurantCompleted = true;
      extra.actualPickupTime = serverTimestamp();
    }
    if (status === DriverOrderStatus.DELIVERED || status === DriverOrderStatus.COMPLETED) {
      extra.actualDeliveryTime = serverTimestamp();
      extra.completedAt = serverTimestamp();
    }
    await updateOrderStatus(orderId, orderStatus, extra);
  }

  if (!order) return;

  switch (status) {
    case DriverOrderStatus.ARRIVED_AT_RESTAURANT:
      await notifyRestaurant(order, '🚗 Driver Arrived',
        `The driver has arrived at your restaurant for order #${order.orderNumber}.`, 'driver_arrived');
      break;
    case DriverOrderStatus.PICKED_UP_ORDER:
      await notifyCustomer(order, '📦 Order Picked Up',
        `Your order #${order.orderNumber} has been picked up and is on the way!`, 'driver_picked_up', OrderStatus.PICKED_UP);
      // iOS createRestaurantDriverPickedUpNotification
      await notifyRestaurant(order, '📦 Order Picked Up',
        `The driver has picked up order #${order.orderNumber} and is on the way to the customer.`, 'driver_picked_up');
      break;
    case DriverOrderStatus.ON_MY_WAY:
    case DriverOrderStatus.IN_DELIVERY:
      await notifyCustomer(order, '🚚 Order On The Way',
        `Your order #${order.orderNumber} is on its way to you!`, 'driver_on_the_way', OrderStatus.IN_DELIVERY);
      break;
    case DriverOrderStatus.ARRIVED_AT_CUSTOMER:
      // iOS attaches the arrival photo: notification.data["arrivalImageUrl"]
      // is what the customer app reads to show the doorstep picture.
      await notifyCustomer(order, '📍 Driver Arrived',
        `Your driver has arrived with order #${order.orderNumber}!`, 'driver_arrived', OrderStatus.ARRIVED,
        extra.arrivalImageUrl ? { arrivalImageUrl: extra.arrivalImageUrl } : undefined);
      break;
    case DriverOrderStatus.DELIVERED:
    case DriverOrderStatus.COMPLETED:
      await notifyCustomer(order, '✅ Order Delivered',
        `Your order #${order.orderNumber} has been delivered. Enjoy!`, 'delivery_completed', OrderStatus.DELIVERED,
        extra.arrivalImageUrl ? { arrivalImageUrl: extra.arrivalImageUrl } : undefined);
      await recordDriverCompletion(orderId);
      // iOS removes the accepted-order doc once delivered.
      await deleteDoc(acceptedRef).catch(() => {});
      break;
    default:
      break;
  }
}

// Records the delivery on drivers/{driverId} exactly once. Runs in a
// transaction keyed off the order doc so retries/double-taps can't
// double-count earnings or deliveries.
//
// Driver payout contract (iOS):
//   creation: driverPayout = deliveryFee * 0.90        (no tip)
//   accept:   driverPayout = deliveryFee * 0.90 + tip  (includes tip)
// Some orders predate these fields or were written by older clients, so
// the recorded amount is max(stored driverPayout, fee*0.9 + tip).
// The driver doc keeps `earnings` (documented schema) and `totalEarnings`
// (legacy web field) in sync.
async function recordDriverCompletion(orderId) {
  const orderRef = doc(db, 'orders', orderId);
  await runTransaction(db, async (tx) => {
    const orderSnap = await tx.get(orderRef);
    if (!orderSnap.exists()) return;
    const o = orderSnap.data();
    if (o.driverEarningsCounted === true || !o.driverId) return;

    const fee = Number(o.deliveryFee) || 0;
    const tip = Number(o.tipAmount) || 0;
    const stored = Number.isFinite(Number(o.driverPayout)) ? Number(o.driverPayout) : 0;
    const amount = Math.max(stored, round2(fee * 0.9 + tip));

    tx.update(orderRef, { driverEarningsCounted: true });

    const driverRef = doc(db, 'drivers', o.driverId);
    const driverSnap = await tx.get(driverRef);
    const d = driverSnap.exists() ? driverSnap.data() : {};
    const newTotal = round2((Number(d.earnings) || Number(d.totalEarnings) || 0) + amount);

    // Cumulative stats the driver profile reads (onTimeRate,
    // averageDeliveryTime). Counters are stored so running averages stay
    // correct — recomputing from orders is impossible once admin cleanup
    // or retention deletes old order docs.
    const nowMs = Date.now();
    const createdMs = o.createdAt?.toMillis?.()
      ?? (o.createdAt instanceof Date ? o.createdAt.getTime() : null);
    const deliverySec = createdMs ? Math.max(0, Math.round((nowMs - createdMs) / 1000)) : null;
    const secTotal = (Number(d.totalDeliverySeconds) || 0) + (deliverySec ?? 0);
    const timedCount = (Number(d.timedDeliveries) || 0) + (deliverySec != null ? 1 : 0);

    const estMs = o.estimatedDeliveryTime?.toMillis?.()
      ?? (o.estimatedDeliveryTime instanceof Date ? o.estimatedDeliveryTime.getTime() : null);
    const onTime = estMs ? nowMs <= estMs + 15 * 60 * 1000 : null;
    const ratedCount = (Number(d.ratedDeliveries) || 0) + (onTime != null ? 1 : 0);
    const onTimeCount = (Number(d.onTimeDeliveries) || 0) + (onTime === true ? 1 : 0);

    tx.set(driverRef, {
      earnings: newTotal,
      totalEarnings: newTotal,
      totalDeliveries: (Number(d.totalDeliveries) || 0) + 1,
      ...(timedCount > 0 ? {
        totalDeliverySeconds: secTotal,
        timedDeliveries: timedCount,
        averageDeliveryTime: Math.round(secTotal / timedCount),
      } : {}),
      ...(ratedCount > 0 ? {
        ratedDeliveries: ratedCount,
        onTimeDeliveries: onTimeCount,
        onTimeRate: onTimeCount / ratedCount,
      } : {}),
      // iOS stops tracking on delivery — currentOrderId clears when this
      // order was the tracked one.
      ...(d.currentOrderId === orderId ? { currentOrderId: null } : {}),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}

// ---------- QR receipt ----------

// Payload format matches iOS QRCodeHelper exactly — timestamp is Unix
// SECONDS (iOS uses Date().timeIntervalSince1970; the CF does ts*1000).
export function buildOrderQRPayload(order) {
  return JSON.stringify({
    orderId: order.id,
    orderNumber: order.orderNumber,
    restaurantId: order.restaurantId,
    timestamp: Math.floor(Date.now() / 1000),
    type: 'order_receipt',
  });
}

// Generates QR data, writes qrCodeData/qrGeneratedAt/qrCodeActive to the order.
export async function generateReceiptQR(order) {
  const QRCode = (await import('qrcode')).default;
  const payload = buildOrderQRPayload(order);
  const dataUrl = await QRCode.toDataURL(payload, { width: 512, margin: 2 });
  await updateDoc(doc(db, 'orders', order.id), {
    qrCodeData: payload,
    qrGeneratedAt: serverTimestamp(),
    qrCodeActive: true,
  });
  return { payload, dataUrl };
}

// Driver-side validation via the deployed callable. Signature matches
// functions/index.js: {qrCodeData, orderId, driverId, manualEntry}.
// The function lives in us-central1 — try it first, fall back to us-east1
// in case a copy is ever deployed there.
export async function validateOrderQRCode({ qrCodeData, orderId, driverId, manualEntry = false }) {
  const payload = { qrCodeData, orderId, driverId, manualEntry };
  try {
    const fn = httpsCallable(functions, 'validateOrderQRCode');
    return (await fn(payload)).data;
  } catch (e1) {
    try {
      const fn = httpsCallable(functionsEast1, 'validateOrderQRCode');
      return (await fn(payload)).data;
    } catch (e2) {
      throw e1;
    }
  }
}
